/**
 * Sends Alia pop-up and tab events to Heap.
 * https://help.aliapopups.com/en/articles/11071697-how-to-use-the-alia-sdk
 *
 * Full pop-up events come from Alia's documented `alia:*` events. Alia has no events for the
 * minimized tab, so tab events and the trigger type come from `window.alia.mediator`, Alia's
 * undocumented store of user actions. If Alia changes it, tab events stop and the documented
 * events keep working.
 *
 * Heap is loaded by consent-gated-loader.js and often doesn't exist yet when the pop-up shows,
 * so events wait in `pending` until Heap loads.
 */
(function aliaHeapTracking() {
  /**
   * @typedef {{ type?: string; triggerType?: string }} AliaReason
   * @typedef {{ type?: string; createdAt?: string; flowID?: number; segmentID?: number; data?: { type?: string; isFloatingButton?: boolean; reason?: AliaReason } }} AliaAction
   * @typedef {{ actions?: AliaAction[] }} AliaState
   * @typedef {{ state?: AliaState; subscribe?: (callback: (state: AliaState) => void) => void }} AliaMediator
   * @typedef {{ popupID?: number; popupTitle?: string; campaignID?: number; campaignTitle?: string; email?: string }} AliaDetail
   * @typedef {{ step?: 'full' | 'tab' | 'closed'; entry?: 'full' | 'tab'; triggerType?: string }} FlowState
   */

  const w =
    /** @type {Window & { heap?: { track?: (name: string, properties: Record<string, unknown>) => void }; alia?: { mediator?: AliaMediator }; onAliaReady?: unknown }} */ (
      window
    );

  /** @type {{ name: string; properties: Record<string, unknown> }[]} */
  let pending = [];
  /** @type {(number | undefined)[]} */
  let submittedPopupIds = [];
  /** @type {Record<string, FlowState>} */
  let flows = {};
  /** @type {Record<string, { popupTitle?: string; campaignTitle?: string }>} */
  let titles = {};
  /** @type {string[]} */
  let seenActionKeys = [];
  let pageStart = performance.timeOrigin;
  let connected = false;

  function flush() {
    const track = w.heap?.track;
    if (typeof track !== 'function') return;
    pending.forEach(({ name, properties }) => track.call(w.heap, name, properties));
    pending = [];
  }

  /**
   * @param {string} name
   * @param {Record<string, unknown>} properties
   */
  function track(name, properties) {
    pending = [...pending, { name, properties }];
    flush();
  }

  /** @param {number | undefined} popupID */
  function getFlow(popupID) {
    return flows[String(popupID)] ?? {};
  }

  /**
   * @param {number | undefined} popupID
   * @param {FlowState} changes
   */
  function updateFlow(popupID, changes) {
    flows = { ...flows, [String(popupID)]: { ...getFlow(popupID), ...changes } };
  }

  /**
   * @param {Event} event
   * @returns {AliaDetail}
   */
  function getDetail(event) {
    return event instanceof CustomEvent && event.detail ? event.detail : {};
  }

  /**
   * Alia's internal actions carry IDs only, so titles for tab events come from earlier SDK events.
   * @param {AliaDetail} detail
   */
  function rememberTitles(detail) {
    if (detail.popupID === undefined) return detail;
    titles = {
      ...titles,
      [String(detail.popupID)]: {
        popupTitle: detail.popupTitle,
        campaignTitle: detail.campaignTitle,
      },
    };
    return detail;
  }

  /**
   * @param {'full' | 'tab'} popupType
   * @param {AliaDetail} detail
   */
  function toProperties(popupType, detail) {
    const cached = titles[String(detail.popupID)] ?? {};
    return {
      popup_type: popupType,
      popup_id: detail.popupID,
      popup_title: detail.popupTitle ?? cached.popupTitle,
      campaign_id: detail.campaignID,
      campaign_title: detail.campaignTitle ?? cached.campaignTitle,
      trigger_type: getFlow(detail.popupID).triggerType,
    };
  }

  /**
   * @param {string} name
   * @param {AliaAction} action
   */
  function trackTab(name, action) {
    track(name, toProperties('tab', { popupID: action.flowID, campaignID: action.segmentID }));
  }

  /**
   * Moves a pop-up's state forward by one Alia action, emitting tab events for new actions.
   * @param {AliaAction} action
   * @param {boolean} isNew
   */
  function applyAction(action, isNew) {
    const { flowID, data } = action;
    const previous = getFlow(flowID);

    if (data?.type === 'popupClose') {
      updateFlow(flowID, { step: 'closed' });
      if (isNew && previous.step === 'tab') trackTab('Alia Tab Closed', action);
      return;
    }
    if (data?.type !== 'popupStep') return;

    const reason = data.reason ?? {};
    const isAutomatic = reason.type === 'trigger' || reason.type === 'programmatic';
    const triggerType = reason.type === 'trigger' ? reason.triggerType : reason.type;

    if (data.isFloatingButton) {
      updateFlow(flowID, { step: 'tab', ...(isAutomatic ? { triggerType } : {}) });
      if (isNew && previous.step !== 'tab') trackTab('Alia Tab Shown', action);
      return;
    }
    if (isAutomatic) {
      updateFlow(flowID, { step: 'full', entry: 'full', triggerType });
      return;
    }
    if (previous.step === 'tab') {
      updateFlow(flowID, { step: 'full', entry: 'tab' });
      if (isNew) trackTab('Alia Tab Opened', action);
      return;
    }
    updateFlow(flowID, { step: 'full' });
  }

  /** @param {AliaAction} action */
  function isFromThisPage(action) {
    return Date.parse(action.createdAt ?? '') >= pageStart;
  }

  /** @param {AliaAction} action */
  function getActionKey(action) {
    return `${action.type}|${action.createdAt}`;
  }

  /** @param {AliaAction[]} actions */
  function handleNewActions(actions) {
    actions
      .filter((action) => isFromThisPage(action) && !seenActionKeys.includes(getActionKey(action)))
      .forEach((action) => {
        seenActionKeys = [...seenActionKeys, getActionKey(action)];
        applyAction(action, true);
      });
  }

  // Alia replays earlier pages' actions on load, unsorted. They only rebuild state (e.g. a tab
  // minimized on a previous page); events come from actions created on this page.
  function connect() {
    const mediator = w.alia?.mediator;
    if (connected || typeof mediator?.subscribe !== 'function') return;
    connected = true;

    const actions = mediator.state?.actions ?? [];
    actions
      .filter((action) => !isFromThisPage(action))
      .sort((a, b) => Date.parse(a.createdAt ?? '') - Date.parse(b.createdAt ?? ''))
      .forEach((action) => applyAction(action, false));
    handleNewActions(actions);
    mediator.subscribe((state) => handleNewActions(state.actions ?? []));
  }

  function connectWhenAliaIsReady() {
    if (w.alia?.mediator) {
      connect();
      return;
    }
    const existing = w.onAliaReady;
    const callbacks = Array.isArray(existing) ? existing : existing ? [existing] : [];
    // Alia runs these callbacks just before it assigns window.alia, so wait a microtask.
    w.onAliaReady = [...callbacks, () => queueMicrotask(connect)];
  }

  document.addEventListener('alia:campaignMatched', (event) => {
    rememberTitles(getDetail(event));
    connect();
  });

  // Reopening the pop-up from the tab also fires popupView; that is already tracked as Tab Opened.
  document.addEventListener('alia:popupView', (event) => {
    const detail = rememberTitles(getDetail(event));
    connect();
    if (getFlow(detail.popupID).entry === 'tab') return;
    track('Alia Popup Shown', toProperties('full', detail));
  });

  document.addEventListener('alia:popupClose', (event) => {
    const detail = rememberTitles(getDetail(event));
    connect();
    track('Alia Popup Closed', toProperties(getFlow(detail.popupID).entry ?? 'full', detail));
  });

  // Alia fires `signup` again for later steps (e.g. SMS) with the stored email attached,
  // so only the first email signup per pop-up per page counts.
  document.addEventListener('alia:signup', (event) => {
    const detail = rememberTitles(getDetail(event));
    if (!detail.email || submittedPopupIds.includes(detail.popupID)) return;
    submittedPopupIds = [...submittedPopupIds, detail.popupID];
    connect();
    track('Alia Email Submitted', toProperties(getFlow(detail.popupID).entry ?? 'full', detail));
  });

  // Alia reloads its stored actions when a page is restored from the back/forward cache;
  // anything created before the restore was already handled on the page that created it.
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) pageStart = Date.now();
  });

  // The loader bootstraps Heap in a microtask after consent-ready, so flush on the next task.
  window.addEventListener('consent-ready', () => setTimeout(flush, 0));

  connectWhenAliaIsReady();
})();
